/**
 * LoadPlanToCalendarButton — renders a "Load into Calendar" button after a
 * coach-generated training plan, with a preview dialog and confirmation flow.
 *
 * TASK-006: Push generated training plans to calendar.
 */

import { useState, useMemo } from 'react';
import { CalendarPlus, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/overlay/dialog';
import {
  applyCalendarSessions,
  type CalendarSessionInput,
  type CoachChatResponse,
} from '@/lib/api';
import { extractCalendarSessions } from '@/lib/loadPlanUtils';
import { useQueryClient } from '@tanstack/react-query';
import { log } from '@/lib/logger';

const TEAL = '0, 255, 200';
const AMBER = '255, 180, 60';
const GREY_TEXT = '#A0A0A0';
const WHITE_TEXT = '#E8E8E8';

/** Map workout type to a display colour. */
function sessionColour(workoutType: string): string {
  const t = workoutType.toLowerCase();
  if (t.includes('long')) return `rgba(${TEAL}, 0.85)`;
  if (t.includes('tempo') || t.includes('threshold')) return `rgba(${AMBER}, 0.85)`;
  if (t.includes('interval') || t.includes('speed') || t.includes('vo2')) return 'rgba(240, 130, 130, 0.85)';
  if (t.includes('rest') || t.includes('recovery')) return 'rgba(100,116,139,0.55)';
  return GREY_TEXT;
}

/** Format ISO date to short weekday name. */
function weekdayLabel(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00');
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] ?? iso.slice(0, 10);
  } catch {
    return iso.slice(0, 10);
  }
}

/** Format ISO date to human-readable. */
function formatDate(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

interface LoadPlanToCalendarButtonProps {
  /** The coach response containing the plan to load. */
  response: CoachChatResponse;
  /** Optional compact mode for the floating widget. */
  compact?: boolean;
}

type ApplyState = 'idle' | 'previewing' | 'applying' | 'success' | 'error';

export function LoadPlanToCalendarButton({ response, compact }: LoadPlanToCalendarButtonProps) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ApplyState>('idle');
  const [resultMessage, setResultMessage] = useState('');

  const sessions = useMemo(() => extractCalendarSessions(response), [response]);

  if (sessions.length === 0) return null;

  const handleApply = async () => {
    setState('applying');
    try {
      const result = await applyCalendarSessions(sessions);
      log.debug('COACH', 'Calendar sessions applied', result);

      // Invalidate calendar queries to refresh views
      queryClient.invalidateQueries({ queryKey: ['seasonSummary'] });
      queryClient.invalidateQueries({ queryKey: ['calendarRange'] });
      queryClient.invalidateQueries({ queryKey: ['calendarWeek'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendar', 'month'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendarToday'] });
      queryClient.invalidateQueries({ queryKey: ['calendarSeason'] });

      if (result.saved > 0) {
        setResultMessage(`${result.saved} session${result.saved > 1 ? 's' : ''} added to your calendar.${result.skipped > 0 ? ` ${result.skipped} duplicate${result.skipped > 1 ? 's' : ''} skipped.` : ''}`);
      } else {
        setResultMessage('All sessions already exist in your calendar.');
      }
      setState('success');
    } catch (err) {
      log.error('COACH', 'Failed to apply calendar sessions', err);
      setResultMessage('Failed to add sessions. Please try again.');
      setState('error');
    }
  };

  const handleClose = () => {
    setState('idle');
    setResultMessage('');
  };

  return (
    <>
      <Button
        variant="outline"
        size={compact ? 'sm' : 'default'}
        onClick={() => setState('previewing')}
        className={compact
          ? 'text-xs font-mono border-white/[0.08] text-emerald-400 hover:text-emerald-300 hover:bg-white/[0.04] mt-1'
          : 'font-mono text-xs border-white/[0.08] text-emerald-400 hover:text-emerald-300 hover:bg-white/[0.04] mt-2'
        }
      >
        <CalendarPlus className={compact ? 'h-3 w-3 mr-1' : 'h-3.5 w-3.5 mr-1.5'} />
        Load into Calendar
      </Button>

      <Dialog open={state !== 'idle'} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <DialogContent className="bg-[rgba(12,16,24,0.97)] border-white/[0.08] backdrop-blur-xl max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm uppercase tracking-wider" style={{ color: WHITE_TEXT }}>
              {state === 'success' ? 'Sessions Added' : state === 'error' ? 'Error' : 'Preview Sessions'}
            </DialogTitle>
          </DialogHeader>

          {/* Preview state */}
          {(state === 'previewing' || state === 'applying') && (
            <>
              <p className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
                {sessions.length} session{sessions.length > 1 ? 's' : ''} will be added to your calendar.
                Existing sessions on the same date won&apos;t be overwritten.
              </p>

              <div className="overflow-y-auto max-h-[50vh] space-y-0.5 pr-1">
                {sessions.map((s, i) => (
                  <div
                    key={`${s.date}-${i}`}
                    className="flex items-baseline gap-2 px-2.5 py-1.5 rounded"
                    style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.04)' }}
                  >
                    <span
                      className="font-mono text-[9px] uppercase tracking-wider w-7 flex-shrink-0"
                      style={{ color: 'rgba(160,160,160,0.45)' }}
                    >
                      {weekdayLabel(s.date)}
                    </span>
                    <span
                      className="font-mono text-[10px] w-14 flex-shrink-0"
                      style={{ color: GREY_TEXT }}
                    >
                      {formatDate(s.date)}
                    </span>
                    <span
                      className="font-mono text-[11px] font-medium w-20 flex-shrink-0 capitalize"
                      style={{ color: sessionColour(s.workout_type) }}
                    >
                      {s.workout_type}
                    </span>
                    {s.duration_or_structure && (
                      <span className="font-mono text-[10px] flex-1 truncate" style={{ color: GREY_TEXT }}>
                        {s.duration_or_structure}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <DialogFooter className="gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClose}
                  disabled={state === 'applying'}
                  className="font-mono text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={state === 'applying'}
                  className="font-mono text-xs"
                  style={{
                    background: `rgba(${TEAL}, 0.12)`,
                    border: `1px solid rgba(${TEAL}, 0.3)`,
                    color: `rgba(${TEAL}, 0.95)`,
                  }}
                >
                  {state === 'applying' ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Adding…
                    </>
                  ) : (
                    <>
                      <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />
                      Add {sessions.length} Session{sessions.length > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Success state */}
          {state === 'success' && (
            <>
              <div className="flex flex-col items-center gap-3 py-4">
                <CheckCircle2 className="h-8 w-8" style={{ color: `rgba(${TEAL}, 0.8)` }} />
                <p className="font-mono text-sm text-center" style={{ color: WHITE_TEXT }}>
                  {resultMessage}
                </p>
              </div>
              <DialogFooter>
                <Button
                  size="sm"
                  onClick={handleClose}
                  className="font-mono text-xs"
                  style={{
                    background: `rgba(${TEAL}, 0.12)`,
                    border: `1px solid rgba(${TEAL}, 0.3)`,
                    color: `rgba(${TEAL}, 0.95)`,
                  }}
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Error state */}
          {state === 'error' && (
            <>
              <div className="flex flex-col items-center gap-3 py-4">
                <AlertTriangle className="h-8 w-8 text-amber-400" />
                <p className="font-mono text-sm text-center" style={{ color: WHITE_TEXT }}>
                  {resultMessage}
                </p>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClose}
                  className="font-mono text-xs border-white/[0.08] text-slate-400"
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  className="font-mono text-xs"
                  style={{
                    background: `rgba(${AMBER}, 0.12)`,
                    border: `1px solid rgba(${AMBER}, 0.3)`,
                    color: `rgba(${AMBER}, 0.95)`,
                  }}
                >
                  Retry
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
